import produce from 'immer';

import * as fromActions from './service-appointment.actions';
import * as fromAdapter from './service-appointment.adapter';
import {VehicleModelYearList} from '../../shared/models/series-details';
import {
  AppointmentStatus,
  IAsyncData,
  IAvailability,
  IAvailabilityOptions,
  IServiceAppointment, IOpcodeResponse,
  IServiceRequest,
  IOverview,
  IZipCodeResponse,
  factoryRecommendedServices
} from '@signal/asp-data-commons';
import { CustomerType } from '../../shared/enums/customer-types.enum';

import {fromAvailabilityOptions, getDateTime} from '../app.adapter';
import { countryCd } from '../../shared/services/util.service';


export function serviceAppointmentReducer(
  state: ServiceAppointmentState = initialState,
  action: fromActions.ServiceAppointmentType): ServiceAppointmentState {

  switch (action.type) {
    case fromActions.ServiceAppointmentActionTypes.CREATE_SERVICE_APPOINTMENT: {
      return produce(state, draftState => {
        draftState.appointmentForm.isEditMode = false;
        draftState.appointmentForm.editDetails = undefined;
        draftState.appointmentForm = initialAppointment;
      });
    }

    case fromActions.ServiceAppointmentActionTypes.EDIT_SERVICE_APPOINTMENT: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.EditServiceAppointment ? action.payload : undefined;
        draftState.appointmentForm = fromAdapter.appointmentToAppointmentFormProjector(payload.appointment,payload.isDashboardRoutePath);
        draftState.appointmentForm.currentStep = 0;
        draftState.appointmentForm.isEditMode = true;
        draftState.appointmentForm.editDetails = {...payload,appointment:fromAdapter.editAppointmentDetails(payload.appointment)};
      });
    }

    case fromActions.ServiceAppointmentActionTypes.UPDATE_SERVICE_FORM: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.UpdateServiceForm ? action.payload : undefined;
        draftState.appointmentForm[payload.path] = payload.value;
      });
    }

    case fromActions.ServiceAppointmentActionTypes.UPDATE_CUSTOMER_FORM_TRANSPORT: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.UpdateCustomerFormTransport ? action.payload : undefined;
        draftState.appointmentForm['customerForm'].transport = payload.transportData;
        draftState.appointmentForm['customerForm'].isRemoteLocation = false;
      });
    }

    case fromActions.ServiceAppointmentActionTypes.UPDATE_CURRENT_STEP: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.UpdateCurrentStep ? action.payload : undefined;
        draftState.appointmentForm.currentStep = payload?.step;
      });
    }

    case fromActions.ServiceAppointmentActionTypes.UPDATE_RECALL_CODES: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.UpdateRecallCodes ? action.payload : undefined;
        draftState.appointmentForm.recallCodes = payload.recallCodes;
      });
    }

    case fromActions.ServiceAppointmentActionTypes.LOAD_MODEL_YEAR_AND_MODELS: {
      const payload = action instanceof fromActions.LoadModelYearAndModels ? action.payload : undefined;
      /* If no need to force reload AND if service requests data is already loaded, return the current state */
      if (!payload.forceReload && state.modelYearAndModels.loaded) {
        return state;
      }
      return produce(state, draftState => {
        draftState.modelYearAndModels = {
          data: {} as IToyotaLexusModelDetails,
          loading: true,
          loaded: false,
          error: ''
        };
      });
    }

    case fromActions.ServiceAppointmentActionTypes.LOAD_MODEL_YEAR_AND_MODELS_SUCCESS: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadModelYearAndModelsSuccess ? action.payload : undefined;
        draftState.modelYearAndModels = {
          data: payload.vehicleModelYearList,
          loading: false,
          loaded: true,
          error: ''
        };
      });
    }

    case fromActions.ServiceAppointmentActionTypes.LOAD_MODEL_YEAR_AND_MODELS_FAIL: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadModelYearAndModelsFail ? action.payload : undefined;
        draftState.modelYearAndModels = {
          data: {} as IToyotaLexusModelDetails,
          loading: false,
          loaded: false,
          error: payload.error
        };
      });
    }

    case fromActions.ServiceAppointmentActionTypes.LOAD_SERVICE_REQUESTS: {
      const payload = action instanceof fromActions.LoadServiceRequests ? action.payload : undefined;
      return produce(state, draftState => {
        draftState.serviceRequests = {
          data: state.serviceRequests.data,
          loading: true,
          loaded: false,
          error: ''
        };
      });
    }

    case fromActions.ServiceAppointmentActionTypes.LOAD_SERVICE_REQUESTS_SUCCESS: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadServiceRequestsSuccess ? action.payload : undefined;
        draftState.serviceRequests = {
          data: payload.opcodeResponse,
          loading: false,
          loaded: true,
          error: ''
        };
      });
    }

    case fromActions.ServiceAppointmentActionTypes.LOAD_SERVICE_REQUESTS_FAIL: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadServiceRequestsFail ? action.payload : undefined;
        draftState.serviceRequests = {
          data: {recommended: [], maintenance: [], complaint: [], dms: []},
          loading: false,
          loaded: false,
          error: payload.error
        };
      });
    }

    case fromActions.ServiceAppointmentActionTypes.LOAD_AVAILABLE_MILEAGES: {
      return produce(state, draftState => {
        draftState.availableMileages = {
          data: [],
          loading: true,
          loaded: false,
          error: ''
        };
      });
    }

    case fromActions.ServiceAppointmentActionTypes.LOAD_AVAILABLE_MILEAGES_SUCCESS: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadAvailableMileagesSuccess ? action.payload : undefined;
        draftState.availableMileages = {
          data: payload.availableMileages,
          loading: false,
          loaded: true,
          error: ''
        };
      });
    }

    case fromActions.ServiceAppointmentActionTypes.LOAD_AVAILABLE_MILEAGES_FAIL: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadServiceRequestsFail ? action.payload : undefined;
        draftState.availableMileages = {
          data: [],
          loading: false,
          loaded: false,
          error: payload.error
        };
      });
    }

    case fromActions.ServiceAppointmentActionTypes.LOAD_AVAILABILITY_OPTIONS: {
      const payload = action instanceof fromActions.LoadAvailabilityOptions ? action.payload : undefined;
      /* If no need to force reload AND if service requests data is already loaded, return the current state */
      if (!payload.forceReload && state.availabilityOptions.loaded) {
        return state;
      }
      return produce(state, draftState => {
        draftState.availabilityOptions = {
          data: {} as IAvailabilityOptions,
          loading: true,
          loaded: false,
          error: ''
        };
      });
    }

    case fromActions.ServiceAppointmentActionTypes.LOAD_AVAILABILITY_OPTIONS_SUCCESS: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadAvailabilityOptionsSuccess ? action.payload : undefined;
        draftState.availabilityOptions = {
          data: payload.availabilityOptions,
          loading: false,
          loaded: true,
          error: ''
        };

        if (!state.appointmentForm.isEditMode) {
          draftState.appointmentForm.availabilityForm = fromAvailabilityOptions(payload.availabilityOptions);
        }
      });
    }

    case fromActions.ServiceAppointmentActionTypes.LOAD_AVAILABILITY_OPTIONS_FAIL: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadAvailabilityOptionsFail ? action.payload : undefined;
        draftState.availabilityOptions = {
          data: {} as IAvailabilityOptions,
          loading: false,
          loaded: false,
          error: payload.error
        };
      });
    }

    case fromActions.ServiceAppointmentActionTypes.LOAD_AVAILABILITY: {
      return produce(state, draftState => {
        draftState.availability = {
          data: state.availability.data,
          loading: true,
          loaded: false,
          error: ''
        };
      });
    }

    case fromActions.ServiceAppointmentActionTypes.LOAD_AVAILABILITY_SUCCESS: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadAvailabilitySuccess ? action.payload : undefined;
        /* Only for date change we should not change the already selected date*/
        const availability = state.appointmentForm?.availabilityForm?.availability;
        if (!(availability?.timeSlotId && availability?.appointmentStartDate)) {
          let dateTime;
          if (!availability.appointmentStartDate){
            dateTime = {
              appointmentStartDate:'', 
              timeSlotId: ''
            }
          }else{
            dateTime = {
              appointmentStartDate: availability.appointmentStartDate,
              timeSlotId: availability.timeSlotId
            };
          }
          draftState.appointmentForm.availabilityForm = {
            availability: {
              ...availability,
              advisorId: payload.loadPayload.advisorId || availability.advisorId,
              ...dateTime,
            }
          };
        }
        draftState.availability = {
          data: payload.availability,
          loading: false,
          loaded: true,
          error: ''
        };

      });
    }

    case fromActions.ServiceAppointmentActionTypes.LOAD_AVAILABILITY_FAIL: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadAvailabilityFail ? action.payload : undefined;
        draftState.availability = {
          data: {} as IAvailability,
          loading: false,
          loaded: false,
          error: payload.error
        };
      });
    }

    case fromActions.ServiceAppointmentActionTypes.RESET_SERVICE_APPOINTMENT_STATE: {
      return initialState;
    }

    case fromActions.ServiceAppointmentActionTypes.RESET_SERVICE_APPOINTMENT_FORM: {
      return produce(state, draftState => {
        draftState.appointmentForm.isEditMode = false;
        draftState.appointmentForm.editDetails = undefined;
        draftState.appointmentForm = initialAppointment;
      });
    }
    case fromActions.ServiceAppointmentActionTypes.LOAD_NOTIFICATION_DASHBOARD_DATA: {
      return produce(state, draftState => {
        draftState.notificationdashboard = {
          data:[] as IOverview[],
          loading: true,
          loaded: false,
          error: ''
        };
      });
    }
    case fromActions.ServiceAppointmentActionTypes.LOAD_NOTIFICATION_DASHBOARD_DATA_SUCCESS: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadNotificationDashboardDataSuccess ? action.payload : undefined;
        draftState.notificationdashboard = {
          data: payload.data,
          loading: false,
          loaded: true,
          error: ''
        };
      });
    }
    case fromActions.ServiceAppointmentActionTypes.LOAD_NOTIFICATION_DASHBOARD_DATA_FAILURE: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadNotificationDashboardDataFailure ? action.payload : undefined;
        draftState.notificationdashboard = {
          data:[] as IOverview[],
          loading: false,
          loaded: false,
          error: payload.error
        };
      });
    }
    case fromActions.ServiceAppointmentActionTypes.LOAD_STATES_CITIES:{
      return produce(state,draftState=>{
        draftState.stateCitiesFromZip = {
          data: [] as IZipCodeResponse[],
          loading: true,
          loaded: false,
          error: ''
        };
      })
    }
    case fromActions.ServiceAppointmentActionTypes.LOAD_STATES_CITIES_SUCCESS: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadStatesAndCitiesSuccess ? action.payload : undefined;
        draftState.stateCitiesFromZip = {
          data: payload.data,
          loading: false,
          loaded: true,
          error: ''
        };
      });
    }

    case fromActions.ServiceAppointmentActionTypes.LOAD_STATES_CITIES_FAILURE: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadStatesAndCitiesFail ? action.payload : undefined;
        draftState.stateCitiesFromZip = {
          data: [] as IZipCodeResponse[],
          loading: false,
          loaded: false,
          error: payload.error
        };
      });
    }
    case fromActions.ServiceAppointmentActionTypes.LOAD_RECOMMENDED_SERVICE:{
      return produce(state,draftState=>{
        draftState.factoryRecommendedService = {
          data: [] as factoryRecommendedServices[],
          loading: true,
          loaded: false,
          error: ''
        };
      })
    }
    case fromActions.ServiceAppointmentActionTypes.LOAD_RECOMMENDED_SERVICE_SUCCESS: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadRecommendedServiceSuccess ? action.payload : undefined;
        draftState.factoryRecommendedService = {
          data: payload.data,
          loading: false,
          loaded: true,
          error: ''
        };
      });
    }

    case fromActions.ServiceAppointmentActionTypes.LOAD_RECOMMENDED_SERVICE_FAIL: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadRecommendedServiceFail ? action.payload : undefined;
        draftState.factoryRecommendedService = {
          data: [] as factoryRecommendedServices[],
          loading: false,
          loaded: false,
          error: payload.error
        };
      });
    }
    case fromActions.ServiceAppointmentActionTypes.TRIGGER_LOADING: {
      return produce(state, draftState => {
        draftState.serviceRequests.loading = true;
      })
    }
    case fromActions.ServiceAppointmentActionTypes.TRIGGER_LOADING_FAIL: {
      return produce(state, draftState => {
        draftState.serviceRequests.loading = false;
      })
    }
    case fromActions.ServiceAppointmentActionTypes.SET_MAKEMODELYR_PAGE_DEFAULT: {
      return produce(state, draftState => {
        draftState.appointmentForm.vehicleForm.isOwnVehicle = false;
      })
    }

    case fromActions.ServiceAppointmentActionTypes.LOAD_PENDING_APPOINTMENTS_COUNT : {
      const payload = action instanceof fromActions.LoadPendingAppointmentCount ? action.payload : undefined;
      return produce(state, draftState => {
        const stateData=JSON.parse(JSON.stringify(state.notificationdashboard.data));
        if (payload.type === 'deletePending') {
          const deletePendingIndex = (stateData.findIndex(val => val.key === 'testdriveDeletePending'));
          if (deletePendingIndex === -1) {
            stateData.push({ key: "testdriveDeletePending", value: payload.count });
          }
          else {
            stateData[deletePendingIndex]['value'] = payload.count;
          }
        }
        else if (payload.type === 'pending') {
          const pendingDeliveryIndex = (stateData.findIndex(val => val.key === 'pendingDelivery'));
          if (pendingDeliveryIndex === -1) {
            stateData.push({ key: "pendingDelivery", value: payload.count });
          }
          else {
            stateData[pendingDeliveryIndex]['value'] = payload.count;
          }
        }
        draftState.notificationdashboard = {
          data: stateData,
          loading: false,
          loaded: true,
          error: ''
        };
      });
    }

    case fromActions.ServiceAppointmentActionTypes.UPDATE_CHECKLIST_COUNT_LOADING: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.UpdateNotificationDashboardOnLoad ? action.payload : undefined;
        const key = payload.key;
        const data = state.notificationdashboard.data.map(fields => {
          if (fields.key === key) {
            return { ...fields, value: payload.count };
          }
          return fields;
        });
        draftState.notificationdashboard.data = data;
      });
    }

    case fromActions.ServiceAppointmentActionTypes.UPDATE_CHECKLIST_COUNT: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.UpdateNotificationDashboard ? action.payload : undefined;
        const key = payload.key;
        const data = state.notificationdashboard.data.map(fields => {
          if (fields.key === key) {
            const value = (fields.value>0) ? (fields.value + payload.count) : 0;
            return { key, value };
          }
          return fields;
        });
        draftState.notificationdashboard.data = data;
      });
    }

    case fromActions.ServiceAppointmentActionTypes.LOAD_SERVICE_APPOINTMENT_FROM_SIGNAL_DEEP_LINK_SUCCESS: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadServiceAppointmentFromSignalDeepLinkSuccess ? action.payload : undefined;
        const vehicleForm = state.appointmentForm.vehicleForm;
        const vehicleDetails = {
          make: (payload.data.vehicle.make)? payload.data.vehicle.make : "",
          otherMake: (payload.data.vehicle.otherMake)? payload.data.vehicle.otherMake : "",
          model: (payload.data.vehicle.model)? payload.data.vehicle.model : "",
          year: (payload.data.vehicle.year)? payload.data.vehicle.year : "",
          vin: payload.data.vehicle.vin
        }
        draftState.appointmentForm.vehicleForm = {
          ...vehicleForm,
          ...vehicleDetails
        }
        
        const customer = payload.data.customer;
        const customerForm = state.appointmentForm.customerForm;
        const customerDetails = {
          firstName: (customer?.firstName) ? customer.firstName : "",
          lastName: (customer?.lastName) ? customer.lastName : "",
          email: (customer?.emailAddress) ?  customer.emailAddress: ""
        }
        if(customer?.phoneNumber) {
          customerDetails['phoneNumber'] = (countryCd === "USA") ? "+1" + customer.phoneNumber : "+52" + customer.phoneNumber
        }
        draftState.appointmentForm.customerForm = {
          ...customerForm,
          ...customerDetails
        };
      });
    }

    default:
      return state;
  }
}

/* Initial state */

const initialAppointment: IServiceAppointmentForm = {
  isEditMode: false,
  editDetails: undefined,
  currentStep: 0,
  appointmentStatus: AppointmentStatus.SCHEDULED,
  recallCodes: [],
  vehicleForm: {
    isOwnVehicle: true,
    isInputVinBased: false,
    vin: '',
    licensePlate: '',
    state: '',
    make: '',
    otherMake: '',
    year: undefined,
    model: '',
    mileage: undefined,
  },
  serviceForm: {
    selectedServices: [],
    isInitialState: true
  },
  availabilityForm: {
    availability: {
      timeSlotId: '',
      advisorId: '',
      languageId: '',
      appointmentStartDate: ''
    }
  },
  customerForm: {
    transport: '',
    remoteZipCode: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    customerType: CustomerType.INDIVIDUAL,
    businessName: '',
    isRemoteLocation: false,
    customerAddress:{
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: ''
     },
     pickUpAddress:{
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: ''
     },
     dropOffAddress:{
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: ''
     },
     isDropOffAddress: false,
    appointmentComments: '',
  },
};

const initialState = {
  appointmentForm: initialAppointment,
  modelYearAndModels: {
    data: {} as IToyotaLexusModelDetails,
    loading: false,
    loaded: false,
    error: ''
  },
  serviceRequests: {
    data: {recommended: [], maintenance: [], complaint: [], dms: []},
    loading: false,
    loaded: false,
    error: ''
  },
  availableMileages: {
    data: [],
    loading: false,
    loaded: false,
    error: ''
  },
  availabilityOptions: {
    data: {} as IAvailabilityOptions,
    loading: false,
    loaded: false,
    error: ''
  },
  availability: {
    data: {} as IAvailability,
    loading: false,
    loaded: false,
    error: ''
  },
  notificationdashboard: {
    data:[] as IOverview[],
    loading: false,
    loaded: false,
    error: ''
  },
  stateCitiesFromZip:{
    data: [] as IZipCodeResponse[],
    loading: false,
    loaded: false,
    error: ''
 },
 factoryRecommendedService:{
  data:[] as factoryRecommendedServices[],
  loading:false,
  loaded:false,
  error:''
}
};

/* Interfaces */

export interface IFooterSummary {
  key: string;
  value: string | number;
  disclaimer?: string;
}

export interface ServiceAppointmentState {
  appointmentForm: IServiceAppointmentForm;
  modelYearAndModels: IAsyncData<{ toyota: VehicleModelYearList, lexus: VehicleModelYearList }>;
  serviceRequests: IAsyncData<IOpcodeResponse>;
  availableMileages: IAsyncData<number[]>;
  availabilityOptions: IAsyncData<IAvailabilityOptions>;
  availability: IAsyncData<IAvailability>;
  notificationdashboard:IAsyncData<IOverview[]>;
  stateCitiesFromZip:IAsyncData<IZipCodeResponse[]>;
  factoryRecommendedService:IAsyncData<factoryRecommendedServices[]>
}

export interface IEditDetails {
  email: string;
  appointmentId: string;
  appointment: IServiceAppointment;
}

export interface IVehicleForm {
  isOwnVehicle: boolean;
  isInputVinBased: boolean;
  description?: string;
  imageUrl?: string;
  vin: string;
  licensePlate?: string;
  state?: string;
  make: string;
  otherMake: string;
  protectionProducts?: string[];
  ppmEligible?: boolean;
  year: string;
  model: string;
  mileage: number;
}

export interface IServiceRequestForm {
  selectedServices: IServiceRequest[];
  isInitialState?: boolean;
}

export interface IAvailabilityForm {
  availability: {
    languageId: string;
    advisorId: string;
    appointmentStartDate: string;
    timeSlotId: string;
  };
}

export interface ICustomerForm {
  transport: string;
  remoteZipCode: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  isRemoteLocation: boolean;
  customerAddress?:IAddress;
  pickUpAddress:IAddress;
  dropOffAddress:IAddress,
  isDropOffAddress: boolean;
  appointmentComments: string;
  customerType?: string;
  businessName?: string;
}

export interface IServiceAppointmentForm {
  currentStep?: number;
  isEditMode?: boolean;
  editDetails?: IEditDetails;
  recallCodes: string[];
  appointmentStatus: AppointmentStatus;
  vehicleForm: IVehicleForm;
  serviceForm: IServiceRequestForm;
  availabilityForm: IAvailabilityForm;
  customerForm: ICustomerForm;
  appointmentId?: string;
}

export interface IToyotaLexusModelDetails {
  toyota: VehicleModelYearList;
  lexus: VehicleModelYearList;
}
export interface IAddress{
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
 }
